import { useMemo } from 'react';
import clsx from 'clsx';
import { buildDaySchedule, type DaySchedule } from '@/domain/queueEngine';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { customerName } from '@/utils/format';
import { format } from 'date-fns';
import DoubleDayBadge from '@/components/DoubleDayBadge';
import { countDoubleDaysSinceLastDayOff } from '@/domain/workload';

interface Props {
  artistId: string;
  dayIso: string;
  /**
   * When provided, the free-window chips render as buttons that pin the chosen
   * window. Absent (the default) keeps the display-only chip behaviour.
   */
  onPickWindow?: (w: { startHour: number; endHour: number }) => void;
}

function formatHour(h: number): string {
  const hr = Math.floor(h);
  const min = Math.round((h - hr) * 60);
  const meridiem = hr >= 12 ? 'pm' : 'am';
  const display = hr % 12 === 0 ? 12 : hr % 12;
  return min === 0 ? `${display}${meridiem}` : `${display}:${min.toString().padStart(2, '0')}${meridiem}`;
}

function periodLabel(schedule: DaySchedule): string {
  if (!schedule.working) return 'Off';
  if (schedule.freeWindows.length === 0) return 'Fully booked';
  const totalFree = schedule.freeWindows.reduce((s, w) => s + w.hours, 0);
  const totalWork = schedule.working.endHour - schedule.working.startHour;
  if (totalFree >= totalWork - 0.5) return 'Whole day open';
  const morning = schedule.freeWindows.some((w) => w.startHour < 12 && w.hours >= 1);
  const afternoon = schedule.freeWindows.some((w) => w.startHour >= 12 && w.hours >= 1);
  if (morning && afternoon) return 'Morning + afternoon open';
  if (morning) return 'Morning open';
  if (afternoon) return 'Afternoon / evening open';
  return 'Partial';
}

export default function ArtistDaySchedule({ artistId, dayIso, onPickWindow }: Props) {
  const artist = useBrainStore((s) => s.artists[artistId]);
  const bookings = useBrainStore((s) => s.bookings);
  const customers = useBrainStore((s) => s.customers);
  const clockIso = useSimulationStore((s) => s.clockIso);

  const schedule = useMemo(
    () => (artist ? buildDaySchedule({ artist, bookings, dayIso }) : null),
    [artist, bookings, dayIso],
  );

  if (!artist || !schedule) return null;

  const doubleDays = countDoubleDaysSinceLastDayOff(artist, Object.values(bookings), clockIso);

  if (!schedule.working) {
    return (
      <section
        data-testid="artist-day-schedule"
        className="rounded-lg border border-gold-dim/20 bg-ink-deep px-4 py-3 mb-4"
      >
        <p className="label-small-caps text-gold-bright">{artist.displayName}&apos;s day</p>
        <p className="font-serif text-cream-soft mt-1">
          {format(new Date(dayIso), 'EEEE d MMM')} · rostered off.
        </p>
      </section>
    );
  }

  const { startHour, endHour } = schedule.working;
  const span = endHour - startHour;
  const period = periodLabel(schedule);

  return (
    <section
      data-testid="artist-day-schedule"
      className="rounded-lg border border-gold-dim/20 bg-ink-deep px-4 py-3 mb-4"
    >
      <header className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="label-small-caps text-gold-bright">
            {artist.displayName} · {format(new Date(dayIso), 'EEE d MMM')}
          </p>
          <p className="font-inter text-[10px] text-cream-faint uppercase tracking-widest mt-1">
            Working {formatHour(startHour)} to {formatHour(endHour)} · {period}
          </p>
          <DoubleDayBadge count={doubleDays} className="mt-1.5" />
        </div>
        <div className="flex items-center gap-3 text-[10px] font-inter uppercase tracking-widest text-cream-faint">
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-sm bg-mint/55" /> Booked
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-sm bg-blood/55" /> Gap to fill
          </span>
        </div>
      </header>

      <div className="mt-3 relative">
        <div className="flex h-9 w-full rounded overflow-hidden border border-gold-dim/30 bg-blood/30">
          {(() => {
            const blocks: React.ReactNode[] = [];
            let cursor = startHour;
            for (const b of schedule.bookings) {
              const blockStart = Math.max(cursor, b.startHour);
              const blockEnd = Math.min(endHour, b.endHour);
              if (blockStart > cursor) {
                const pct = ((blockStart - cursor) / span) * 100;
                blocks.push(
                  <div
                    key={`gap-${cursor}`}
                    style={{ width: `${pct}%` }}
                    title={`${formatHour(cursor)}-${formatHour(blockStart)} gap`}
                    className="bg-blood/55 flex items-center justify-center text-[9px] font-inter uppercase tracking-widest text-cream"
                  >
                    GAP
                  </div>,
                );
              }
              if (blockEnd > blockStart) {
                const pct = ((blockEnd - blockStart) / span) * 100;
                const cust = customers[b.customerId];
                blocks.push(
                  <div
                    key={`book-${b.id}`}
                    style={{ width: `${pct}%` }}
                    title={`${formatHour(b.startHour)}-${formatHour(b.endHour)} · ${cust ? customerName(cust) : b.designBrief} · ${b.designBrief}`}
                    className="bg-mint/55 border-l border-r border-mint/70 flex items-center justify-center text-[9px] font-inter uppercase tracking-widest text-ink-black overflow-hidden whitespace-nowrap px-1"
                  >
                    {cust ? cust.firstName : b.designBrief}
                  </div>,
                );
              }
              cursor = Math.max(cursor, b.endHour);
            }
            if (cursor < endHour) {
              const pct = ((endHour - cursor) / span) * 100;
              blocks.push(
                <div
                  key={`gap-tail-${cursor}`}
                  style={{ width: `${pct}%` }}
                  title={`${formatHour(cursor)}-${formatHour(endHour)} gap`}
                  className="bg-blood/55 flex items-center justify-center text-[9px] font-inter uppercase tracking-widest text-cream"
                >
                  GAP
                </div>,
              );
            }
            return blocks;
          })()}
        </div>
        <div className="flex justify-between mt-1 font-inter text-[9px] text-cream-faint uppercase tracking-widest">
          <span>{formatHour(startHour)}</span>
          {span >= 6 ? <span>{formatHour(startHour + span / 2)}</span> : null}
          <span>{formatHour(endHour)}</span>
        </div>
      </div>

      {schedule.freeWindows.length > 0 ? (
        <div className="mt-3 flex flex-wrap gap-2" data-testid="free-windows">
          {schedule.freeWindows.map((w) => {
            const chipClass = clsx(
              'inline-flex items-center gap-1 px-2 py-1 rounded-sm text-[10px] font-inter uppercase tracking-widest',
              w.hours >= 3 ? 'bg-blood/55 text-cream' : 'bg-gold/55 text-ink-black',
            );
            const label = (
              <>
                {formatHour(w.startHour)} to {formatHour(w.endHour)}{' '}
                <span className="text-cream-faint">({w.hours}h)</span>
              </>
            );
            return onPickWindow ? (
              <button
                key={`${w.startHour}-${w.endHour}`}
                type="button"
                onClick={() => onPickWindow({ startHour: w.startHour, endHour: w.endHour })}
                data-testid={`free-window-${w.startHour}-${w.endHour}`}
                className={clsx(chipClass, 'transition hover:ring-1 hover:ring-gold-bright')}
              >
                {label}
              </button>
            ) : (
              <span key={`${w.startHour}-${w.endHour}`} className={chipClass}>
                {label}
              </span>
            );
          })}
        </div>
      ) : null}
    </section>
  );
}
